import { useEffect, useMemo, useState } from "react";
import { styled } from "styled-components";

import { useChatClass } from "../../theme";
import { Close, CloseContainer } from "./Close";
import { MessageContainer } from "./Message";

type ChatPosition = "left" | "right";

export const ProactiveMessageContainer = styled.div(({ theme }) => {
  return {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginBottom: theme.space[4],
    marginTop: theme.space[3],
    alignItems: "inherit",

    [`& ${MessageContainer}`]: {
      animation: "fadeIn 1500ms ease-out 0ms forwards",
    },
  };
});

export const ProactiveContainer = styled.div(() => {
  return {
    position: "absolute",
    bottom: "100%",
    width: 256,
    display: "flex",
    flexDirection: "column",

    [`& ${CloseContainer}`]: {
      opacity: 0,
    },

    [`&:hover`]: {
      [`& ${CloseContainer}`]: {
        opacity: 1,
      },
    },
  };
});

interface ProactiveQueueProps {
  side: ChatPosition;
  messages: string[];
  /** Callback function when any message previews are clicked.
   * Typically used to open the chat window. */
  onMessageClick?: (...args: any[]) => any;
}

interface ProactiveComponent extends React.FC<ProactiveQueueProps> {
  Message: typeof MessageContainer;
  Close: typeof Close;
}

const Proactive: ProactiveComponent = ({ side, messages, onMessageClick }) => {
  const chatClass = useChatClass();
  const [isClosed, setIsClosed] = useState(false);

  const queue = useMemo(
    () =>
      messages.map((message, index) => {
        return (
          <MessageContainer
            key={index}
            onClick={onMessageClick}
            role="button"
            tabIndex={0}
            aria-label={`Proactive message: ${String(message)}`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onMessageClick?.();
              }
            }}
          >
            {String(message)}
          </MessageContainer>
        );
      }),
    [messages, onMessageClick],
  );

  useEffect(() => {
    if (!queue.length) return;
    setIsClosed(false);
  }, [queue]);

  if (isClosed || !queue.length) return null;

  return (
    <ProactiveContainer
      style={{
        [side]: 0,
        alignItems: side === "left" ? "start" : "end",
      }}
      className={chatClass("proactive")}
      role="region"
      aria-label="Proactive chat messages"
    >
      <Close
        onClick={() => setIsClosed(true)}
        aria-label="Dismiss proactive messages"
      />
      <ProactiveMessageContainer role="list" aria-label="Message previews">
        {queue}
      </ProactiveMessageContainer>
    </ProactiveContainer>
  );
};

Proactive.Message = MessageContainer;
Proactive.Close = Close;

export { Proactive };
